/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved.
 */
import {
  formatPhoneNumberIntl as parentFormatPhoneNumberIntl,
  isPossiblePhoneNumber as parentIsPossiblePhoneNumber
} from 'react-phone-number-input';
import {translate} from '@ion/core/src/locale/locale';

/**
 * Formats the given E.164 phone number into international format.
 * If the given phone number is invalid or undefined it will return an empty string ('').
 *
 * ```typescript
 *  formatPhoneNumber('+12133734253'); // '+1 213 373 4253'
 *  formatPhoneNumber('+');            // ''
 *  formatPhoneNumber('');             // ''
 *  formatPhoneNumber(undefined);      // ''
 * ```
 */
export const formatPhoneNumberIntl = (phoneNumber: string): boolean => {
  return parentFormatPhoneNumberIntl(phoneNumber);
};

/**
 * Determines and returns the corresponding error message based on the given error flags.
 * The empty error is given precedence due to phone number validation logic.
 * If both are false it will return an empty string ('');
 *
 * ```typescript
 *  getPhoneNumberErrorText(true, false);  // Empty Error
 *  getPhoneNumberErrorText(true, true);   // Empty Error
 *  getPhoneNumberErrorText(false, true);  // Validation Error
 *  getPhoneNumberErrorText(false, false); // ''
 * ```
 */
export const getPhoneNumberErrorText = (isEmpty: boolean, hasValidationError: boolean): string => {
  if (isEmpty) return translate('contacts:phone.phoneError');
  if (hasValidationError) return translate('contacts:phone.phoneNumError');
  return '';
};

/**
 * Determines if the provided phone number is a possible phone number (based on length determined by country code).
 *
 * ```typescript
 *  isPossiblePhoneNumber('+12223334444'); // true
 *  isPossiblePhoneNumber('+1222333444');  // false
 *  isPossiblePhoneNumber('+');            // false
 *  isPossiblePhoneNumber('');             // false
 *  isPossiblePhoneNumber(undefined);      // TypeError
 * ```
 */
export const isPossiblePhoneNumber = (phoneNumber: string): boolean => {
  return parentIsPossiblePhoneNumber(phoneNumber);
};

/**
 * Characters that are normally allowed from an html number
 * input that we restrict in this form's number inputs
 */
export const restrictedNumberChars = ['e', 'E', '+', '-', '.'];
